import React, { useCallback } from 'react';
import { IconButton } from '@wix/wix-ui-tpa/cssVars';
import { ChevronLeft, ChevronRight } from '@wix/wix-ui-icons-common/on-stage';
import { useSettings } from '@wix/tpa-settings/react';
import { useBi, useEnvironment, useTranslation } from '@wix/yoshi-flow-editor';
import { useCSSPBEnabled } from '../../../../../hooks/useCSSPBStyle';
import { useSidebarLayoutBase } from '../../views/SidebarLayout/contexts/SidebarLayoutBase/SidebarLayoutBaseContext';
import challengeSettings from '../../../settingsParams';
import { classes, st } from './CollapseSidebarButton.st.css';
import { memberWebAppButtonClick as memberWebAppButtonClickV2 } from '@wix/bi-logger-challenges-member-web/v2';

export interface ICollapseSidebarButtonProps {}

export const CollapseSidebarButton: React.FC<
  ICollapseSidebarButtonProps
> = () => {
  const {
    isSidebarCollapsed,
    isCollapsibleSidebarEnabled,
    toggleSidebarCollapse,
  } = useSidebarLayoutBase();
  const { isMobile } = useEnvironment();
  const { t } = useTranslation();
  const settings = useSettings();
  const cssPBEnabled = useCSSPBEnabled({
    includeParticipantPageAlignmentCheck: true,
  });
  const bi = useBi();
  const clickHandler = useCallback(() => {
    toggleSidebarCollapse();
    bi.report(
      memberWebAppButtonClickV2({
        buttonName: isSidebarCollapsed ? 'expandSidebar' : 'collapseSidebar',
      }),
    );
  }, [bi, isSidebarCollapsed, toggleSidebarCollapse]);

  // Don't render if sidebar is not collapsible or if on mobile
  if (!isCollapsibleSidebarEnabled || isMobile) {
    return null;
  }

  return (
    <IconButton
      className={st(classes.root, {
        align: !cssPBEnabled
          ? settings.get(challengeSettings.sidebarLayoutTextAlignment)
          : null,
      })}
      onClick={clickHandler}
      aria-label={
        isSidebarCollapsed
          ? t('live-site.sidebar.collapse-button.expand.aria-label')
          : t('live-site.sidebar.collapse-button.collapse.aria-label')
      }
      icon={
        isSidebarCollapsed ? (
          <ChevronRight className={classes.icon} />
        ) : (
          <ChevronLeft className={classes.icon} />
        )
      }
    />
  );
};

CollapseSidebarButton.displayName = 'CollapseSidebarButton';
